import { useEffect, useState, createContext, useRef } from "react";
import "./App.css";
import { SideBar } from "./components/sidebar";
import { Dashboard } from "./screens/dashboard";
import { Courses } from "./screens/courses";
import { CreateCourse } from "./screens/createCourse";
import { Students } from "./screens/students";
import { Profile } from "./screens/profile";
import { PublishCourse } from "./screens/publishCourse";
import { Settings } from "./screens/settings";
import { Communication } from "./screens/communication";
import { TopBar } from "./components/topbar";
import { Quiz } from "./screens/quiz";
import { Reports } from "./screens/reports";
import { Orders } from "./screens/orders";
import { Project } from "./screens/workflows"
import { VerticalLayout } from "./components/layout";
import Review from "./screens/review";
import { SearchP } from "./screens/search";
import { getAPI, updateAPI } from "./api/api";
import { handleDocumentValues } from "./functions/handleTheme";
import { LoadingScreen } from "./components/loadingScreen";
import { Toast } from "./atoms/toast";
import CardInfo from "./components/tap-to-learn/CardInfo";

export const PageContext = createContext(1);
export const UserContext = createContext({});
export const ToastContext = createContext({});
export const ThemeContext = createContext({})
function App() {
  const [loading, setLoading] = useState(true);
  const [sidebar, setSideBar] = useState({ navigation: 3, courseID: 0, enrollment: 0, userId: 0 })
  const [activeTheme, setActiveTheme] = useState({})
  const [oldSidebar, setOldSideBar] = useState(1)
  const [credit, setCredit] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const searchRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [userInformation, setUserInformation] = useState({ "id_users": "", "full_name": "-", "email": "-", "permissions": [], "org_settings": { "employee_text": "Werknemers" } });
  const searchParams = new URLSearchParams(window.location.search);
  const preview = searchParams.get("preview");
  const [brandingLogo, setBrandingLogo] = useState("");
  const [alignment, setAlignment] = useState('left');
  const isQuizActive = sidebar.navigation === 10;
  const [toastList, setToastList] = useState([]);
  const [notifiedCourses, setNotifiedCourses] = useState(new Set());
  const [courseToUpdate, setCourseToUpdate] = useState(null);
  const notifiedCoursesRef = useRef(new Set());
  const [isPublic, setIsPublic] = useState(false);
  const [avatarsInProgress, setAvatarsInProgress] = useState([])
  function handleSetEmployeeText(data, setUserInformation) {
    setUserInformation(prev => ({
      ...prev,
      org_settings: {
        ...prev.org_settings,
        employee_text: data.SET_EMPLOYEE_TEXT
      }
    }));
  }
  async function fetchCourseStatus() {
    const res = await getAPI("api/courses/status");
    console.log("courses with status", res)
    return res.data || [];
  }

  async function notifyNewlyReadyCourses(newlyReady, showToast) {
    newlyReady.forEach(course => {
      showToast(`Cursus "${course.title}" is klaar!`, "success");
      setCourseToUpdate(course.id_course);
    });

    await Promise.all(newlyReady.map(course => updateAPI("api/courses/status/update", { id_course: course.id_course, status: 0 })));
  }

  function handleReadyCourses(courses, showToast) {
    const newlyReady = courses.filter(course => course.ready === 1 && !notifiedCourses.has(course.id_course));
    if (newlyReady.length > 0) {
      notifyNewlyReadyCourses(newlyReady, showToast);
      setNotifiedCourses(prevSet => {
        const updated = new Set(prevSet);
        newlyReady.forEach(course => updated.add(course.id_course));
        return updated;
      });
    }
  }

  function handleFailedCourses(courses, showToast) {
    const failedCourses = courses.filter(course => course.ai_mode === 4 && !notifiedCoursesRef.current.has(course.id_course));
    if (failedCourses.length > 0) {
      failedCourses.forEach(course => {
        showToast(`AI-fout bij "${course.title}". Herstel of probeer opnieuw.`, "error");
      });
      failedCourses.forEach(course => notifiedCoursesRef.current.add(course.id_course));
      setNotifiedCourses(new Set(notifiedCoursesRef.current));
    }
  }
  function startCourseStatusPolling(showToast) {
    const interval = setInterval(async () => {
      const courses = await fetchCourseStatus();
      if (!courses || courses.length === 0) {
        clearInterval(interval);
        return;
      }
      handleReadyCourses(courses, showToast);
      handleFailedCourses(courses, showToast);
    }, 15000);

    return () => clearInterval(interval);
  }

  useEffect(() => {

    const stopPolling = startCourseStatusPolling(showToast);
    return () => {
      stopPolling();
      setNotifiedCourses(new Set());
    };
  }, [sidebar.navigation]);

  function handleMessage(event, setUserInformation, setBrandingLogo, setAlignment) {
    if (event.data?.SET_ALIGNMENT) {
      setAlignment(event.data.SET_ALIGNMENT);
    }
    if (event.data?.SET_EMPLOYEE_TEXT) {
      handleSetEmployeeText(event.data, setUserInformation);
    }
    if (event.data?.SET_LOGO) {
      const file = event.data.SET_LOGO;
      if (file instanceof File) {
        const logoUrl = URL.createObjectURL(file);
        setBrandingLogo(logoUrl);
      }
    }
  }

  useEffect(() => {

    if (preview !== "true") return;
    const messageListener = (event) => {
      handleMessage(event, setUserInformation, setBrandingLogo, setAlignment);
    };

    window.addEventListener("message", messageListener);

    return () => {
      window.removeEventListener("message", messageListener);
    };
  }, [preview]);

  const handleLogoClick = (e) => {
    if (preview === "true") {
      e.preventDefault();
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prevState) => !prevState);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "f") {
        event.preventDefault();
        searchRef.current.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1024px)");
    setIsMobile(mediaQuery.matches);

    const handleResize = () => {
      setIsMobile(mediaQuery.matches);
    };

    mediaQuery.addEventListener("change", handleResize);

    return () => {
      mediaQuery.removeEventListener("change", handleResize);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    if (userInformation.account_type !== undefined && userInformation.account_type !== null) {
      setIsPublic(userInformation.account_type === 1);
    }
  }, [userInformation, sidebar.navigation]);

  useEffect(() => {
    if (userInformation.id_users && userInformation.account_type !== undefined) {
      loadThemeData();
    }
  }, [isPublic, sidebar.navigation]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAPI("api/users/@me/details");
      setUserInformation({
        ...response.data.details,
        permissions: response.data.permissions,
        org_settings: { employee_text: "Werknemers" },
      });
      setTimeout(() => {
        setLoading(false);
      }, 3000);
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      setLoading(false);
    }
  };


  useEffect(() => {
    const poll = pollAvatarStatus()
    return (
      clearInterval(poll)
    )
  }, [])

  const loadAvatarStatus = async () => {
    const query = 'api/courses/lessons/modules/avatars/status'
    const loadStatus = await getAPI(query)
    setAvatarsInProgress(loadStatus.data)
    return loadStatus.data
  }
  const pollAvatarStatus = () => {
    const poll = setInterval(() => {
      const status = loadAvatarStatus()
     const new_status =  status.filter(avatar => avatar.progress == 2).map((avatar) => {

        showToast('Avatar is gereed','success')
        return avatar.id_modules
      })
      updateAvatarStatus(new_status)
    }, 10000)
    return poll
  }

  const updateAvatarStatus = async (id_modules) => {
    const query = 'api/courses/lessons/modules/avatars/status/update'
    const id_modules_body = {"modules":id_modules}
    await updateAPI(query, id_modules_body)

  }
  
  async function loadThemeData() {
    const query = "api/business/theme";
    const themeData = await getAPI(query);
    const doc = document;
    let themeToApply;
    if (themeData.data.length === 0 && isPublic) {
      themeToApply = {
        sidebar_style: 2,
        logo_position: "center",
        sidebar: "#587da7",
      };
      setActiveTheme(themeToApply);
    } else {
      themeToApply = themeData.data[0];
      setActiveTheme(themeToApply);
    }
    for (let key in themeToApply) {
      if (themeToApply[key] != null) {
        handleDocumentValues(doc, key, themeToApply[key]);
      }
    }
    if (themeToApply?.employee_text) {
      setUserInformation((prevState) => ({
        ...prevState,
        org_settings: { employee_text: themeToApply.employee_text },
      }));
    }

    if (themeToApply?.logo_url) setBrandingLogo(themeToApply.logo_url);
    if (themeToApply?.logo_position) setAlignment(themeToApply.logo_position);
  }


  function switchPage(newPage) {
    if (newPage === 5 || newPage === 7) {
      if (!sidebar.courseID || sidebar.courseID === 0) {
        console.warn("Blocked navigation: courseID is missing");
        return;
      }
    }

    if (newPage !== 14) {
      setSearchTerm("");
    }

    setOldSideBar(sidebar.navigation);
    if (newPage !== sidebar.navigation) {
      setTimeout(() => {
        handleNavigation({ navigation: newPage, courseID: sidebar.courseID, enrollment: 0, userId: 0 });
      }, 90);
    }
  }
  const handleNavigation = (data) => {
    closeCredit();
    setSideBar({ ...sidebar, ...data })
  }
  const PROFILESCREEN = 6;
  const NAV_COMPS = [
    { navTitle: "Dashboard", navID: 1, icon: "dashboard", permission: "dashboard.view" },
    { navTitle: "Cursussen", navID: 2, icon: "courses" },
    { navTitle: isPublic ? "Leads" : userInformation.org_settings.employee_text, navID: 3, icon: isPublic ? "leads" : "users", permission: "users.view" },
    { navTitle: isPublic ? "Functionaliteiten" : "Bestellingen", navID: 12, icon: "reports", permission: "orders.view" },
    { navTitle: "Rapporten", navID: 11, icon: "analytic", permission: "company.detailedMetrics.view" },
    !isPublic && { navTitle: "Communicatie", navID: 9, icon: "message", permission: "communication.view" },
    { navTitle: isPublic ? "Campagne" : "Workflows", navID: 15, icon: isPublic ? "campagne" : "project" }
  ].filter(Boolean);  

  useEffect(() => {
    var safePage = userInformation.permissions.includes("dashboard.view") ? 1 : 2;
    if (sidebar.navigation === safePage) {
      return;
    }
    const navIDs = NAV_COMPS.map((comp) => ({ navID: comp.navID, permission: comp.permission }));

    navIDs.forEach((comp) => {
      if (sidebar.navigation === comp.navID && comp.permission && !userInformation.permissions.includes(comp.permission)) {
        setSideBar({ ...sidebar, navigation: safePage });
      }
    });
  }, [sidebar]);

  const openCredit = () => {
    setCredit(!credit);
  }
  const closeCredit = () => {
    setCredit(false);
  }
  if (loading) {
    return <LoadingScreen brandingLogo={brandingLogo} fadeOut={!loading} />;
  }

  function showToast(message, type = "general", duration = 5000) {
    const toastId = Date.now();
    setToastList(prev => [...prev, { id: toastId, message, type, duration }]);
    setTimeout(() => {
      setToastList(prev => prev.filter(toast => toast.id !== toastId));
    }, duration);
  }

  return (
    <ThemeContext.Provider value={activeTheme}>
      <UserContext.Provider value={userInformation}>
        <PageContext.Provider value={{ sidebar, handleNavigation }}>
          <ToastContext.Provider value={{ showToast }}>
            <div className="toast-box">
              {toastList.map((i) => {
                return <Toast key={i.id} message={i.message} type={i.type} duration={i.duration} />
              })}
            </div>
            <div className="App grey-background-50">
              <div className={`horizontal complete-height`}>
                {!isQuizActive && (
                  isMobile ? (
                    <SideBar
                      hide={sidebar.navigation === 16}
                      isSidebarOpen={isSidebarOpen}
                      setIsSidebarOpen={setIsSidebarOpen}
                      active={sidebar.navigation}
                      onClick={(comp) => switchPage(comp.navID)}
                      navigationComps={NAV_COMPS}
                      handleLogoClick={handleLogoClick}
                      brandingLogo={brandingLogo}
                      alignment={alignment}
                    />
                  ) : (
                    <SideBar
                      hide={sidebar.navigation === 16}
                      isSidebarOpen={true}
                      setIsSidebarOpen={setIsSidebarOpen}
                      active={sidebar.navigation}
                      onClick={(comp) => switchPage(comp.navID)}
                      navigationComps={NAV_COMPS}
                      handleLogoClick={handleLogoClick}
                      brandingLogo={brandingLogo}
                      alignment={alignment}
                    />
                  ))}

                <VerticalLayout>
                  {/* <Credits active={credit} /> */}
                  <TopBar
                    hide={sidebar.navigation === 16}
                    logo={brandingLogo}
                    isQuizActive={isQuizActive}
                    activateCredit={openCredit}
                    toggleSidebar={toggleSidebar}
                    onSearchChange={(value) => setSearchTerm(value)}
                    searchValue={searchTerm}
                    searchRef={searchRef}
                    user={userInformation}
                    notification={true}
                    goProfile={() =>
                      handleNavigation({
                        navigation: PROFILESCREEN,
                        courseID: 0,
                        enrollment: 0,
                        userId: 0,
                      })
                    }
                  />
                  <Screens
                    AiStarted={() => startCourseStatusPolling(showToast)}
                    courseToUpdate={courseToUpdate}
                    setCourseToUpdate={setCourseToUpdate}
                    screen={sidebar.navigation}
                    oldSidebar={oldSidebar}
                    searchTerm={searchTerm}
                    goToStep={(value) => switchPage(value)}
                    cardInfo={sidebar.cardInfo}
                  />
                </VerticalLayout>
              </div>
            </div>
          </ToastContext.Provider>
        </PageContext.Provider>
      </UserContext.Provider>
    </ThemeContext.Provider>
  );
}

function Screens({ screen, goToStep, oldSidebar, searchTerm, setCourseToUpdate, courseToUpdate, AiStarted ,cardInfo}) {
  const [courseValue, setCourseValue] = useState(0);

  useEffect(() => {
    const debounceSearch = setTimeout(() => {
      if (searchTerm.length > 0 && screen !== 14) {
        goToStep(14);
      } else if (searchTerm.length === 0 && screen === 14) {
        goToStep(oldSidebar);
      }
    }, 100);

    return () => clearTimeout(debounceSearch);
  }, [searchTerm]);

  const [enrollment, setEnrollments] = useState(0);
  switch (screen) {
    case 1:
      return <Dashboard pageSwap={oldSidebar == 1} />;
    case 2:
      return <Courses pageSwap={oldSidebar == 2} courseToUpdate={courseToUpdate} setCourseToUpdate={setCourseToUpdate} AiStarted={AiStarted} />;
    // case 4:
    //   return (
    //     <NewCourse
    //       pageSwap={oldSidebar == 4}
    //       goTo={(value) => {
    //         goToStep(4);
    //         setCourseValue(value);
    //       }}
    //       goToStep={(value) => {
    //         goToStep(value);
    //       }}
    //     />
    //   );
    case 5:
      return <CreateCourse pageSwap={oldSidebar == 5} />;
    case 3:
      return (
        <Students
          pageSwap={oldSidebar == 3}
          goToProfile={(student) => goToStep(6)}
        />
      );
    case 6:
      return (
        <Profile
          pageSwap={oldSidebar == 6}
          switchPage={goToStep}
          oldSidebar={oldSidebar}
        />
      );
    case 7:
      return <PublishCourse pageSwap={oldSidebar == 7} />;
    case 8:
      return <Settings pageSwap={oldSidebar == 8} />;
    case 9:
      return <Communication pageSwap={oldSidebar == 9} />;
    case 10:
      return <Quiz pageSwap={oldSidebar == 10} />;
    case 11:
      return <Reports pageSwap={oldSidebar == 11} />;
    case 12:
      return <Orders pageSwap={oldSidebar == 12} />;
    case 13:
      return <Review pageSwap={oldSidebar == 13} />;
    case 14:
      return (
        <SearchP
          pageSwap={oldSidebar == 14}
          oldPage={oldSidebar}
          goToStep={(s) => goToStep(s)}
          searchTerm={searchTerm}
        />
      );
    case 15:
      return <Project pageSwap={oldSidebar === 15} />;
    case 16:
      return <CardInfo pageSwap={oldSidebar === 16}  cardInfo={cardInfo} />;

  }
}

export default App;
